import type { ApiOrder, Order, SortBy } from '../types';

import { normaliseOrder } from './normalise';
import { isWithinLeadTime, shouldRetain } from './retention';
import { sortOrders } from './sort';
import { isDisplayStatus, laneFor, parseStatus } from './status';

export interface OrderStoreOptions {
  doneRetentionMinutes: number;
  sortBy?: SortBy | null;
  /**
   * Clock consulted for retention and lead-time decisions whenever a caller
   * does not pass `now` explicitly. Defaults to `Date.now`; the live runtime
   * injects the server-anchored clock from `src/clock.ts` so a wrong TV clock
   * can never decide what is on screen.
   */
  now?: () => number;
}

export interface LaneSnapshot {
  left: Order[];
  right: Order[];
}

type Listener = (snapshot: LaneSnapshot) => void;

/**
 * In-memory order index. The store is the single source of truth for what
 * appears on screen. It does not know about the network at all - sockets
 * and polling call upsert/applyStatus/remove; rendering subscribes.
 *
 * Apart from `subscribe()`, every mutating method ends in `notify()`, which
 * is debounced via microtask so a burst of updates collapses into one
 * render.
 */
export class OrderStore {
  private orders = new Map<string, Order>();
  private listeners = new Set<Listener>();
  private pendingNotify = false;
  private readonly clock: () => number;

  constructor(private readonly opts: OrderStoreOptions) {
    this.clock = opts.now ?? (() => Date.now());
  }

  /**
   * Replace the set of orders with a REST snapshot - used by the initial load
   * and by reconciliation. Orders missing from the snapshot are dropped.
   *
   * Newest state wins: a snapshot is a point-in-time read that can land
   * *after* a WebSocket event for the same order (the request was already in
   * flight when the event arrived, or the gateway's read path lags its event
   * path). When the copy we already hold has a later `updatedAt` than the
   * snapshot row we keep ours, so a fresh "Ready" transition is never bounced
   * back to "Preparing" by a stale row. Rows the snapshot reports as
   * cancelled or with an unknown status still drop the order regardless.
   */
  replaceAll(
    apiOrders: ReadonlyArray<ApiOrder | null | undefined>,
    now: number = this.clock(),
  ): void {
    const held = this.orders;
    const next = new Map<string, Order>();
    for (const api of apiOrders) {
      const fresh = normaliseOrder(api);
      if (!fresh) continue;
      const current = held.get(fresh.id);
      next.set(fresh.id, current && isNewer(current, fresh) ? current : fresh);
    }
    this.orders = next;
    // Prune already-expired completed orders so a page reload never re-shows them.
    this.sweep(now);
    this.notify();
  }

  /** Insert or update a single order from a WS `order_created` event. */
  upsert(apiOrder: ApiOrder | null | undefined): void {
    const order = normaliseOrder(apiOrder);
    if (!order) return;
    this.orders.set(order.id, order);
    this.notify();
  }

  /**
   * Apply a status update event. Unknown status -> remove the order.
   * Status `cancelled` -> remove the order. Anything else updates the
   * existing card in place; missing orders are ignored (a reconciliation
   * fetch will eventually pick them up).
   */
  applyStatus(orderId: string, rawStatus: string | null, eventTimestamp?: string): void {
    const current = this.orders.get(orderId);
    if (!current) return;

    const next = parseStatus(rawStatus);
    if (!next || !isDisplayStatus(next)) {
      this.orders.delete(orderId);
      this.notify();
      return;
    }

    if (next === current.status && !eventTimestamp) return;

    const ts =
      eventTimestamp && Date.parse(eventTimestamp) > 0
        ? eventTimestamp
        : current.updatedAt;

    this.orders.set(orderId, {
      ...current,
      status: next,
      lane: laneFor(next),
      updatedAt: ts,
    });
    this.notify();
  }

  remove(orderId: string): void {
    if (this.orders.delete(orderId)) this.notify();
  }

  /**
   * Prune completed-orders whose retention window has expired.
   * Called from a timer in the UI tick.
   */
  sweep(now: number = this.clock()): void {
    let mutated = false;
    for (const [id, order] of this.orders) {
      if (!shouldRetain(order, { doneRetentionMinutes: this.opts.doneRetentionMinutes, now })) {
        this.orders.delete(id);
        mutated = true;
      }
    }
    if (mutated) this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Returns ordered, lead-time-filtered lanes. Pure - safe to call from
   * tests without touching listeners.
   */
  snapshot(now: number = this.clock()): LaneSnapshot {
    const left: Order[] = [];
    const right: Order[] = [];
    for (const order of this.orders.values()) {
      if (!isWithinLeadTime(order, now)) continue;
      if (order.lane === 'left') left.push(order);
      else right.push(order);
    }
    const sortBy = this.opts.sortBy ?? null;
    return { left: sortOrders(left, sortBy), right: sortOrders(right, sortBy) };
  }

  /** Test / debugging helper. */
  size(): number {
    return this.orders.size;
  }

  private notify(): void {
    if (this.pendingNotify) return;
    this.pendingNotify = true;
    // Microtask debounce - batches a burst of upserts into one render.
    Promise.resolve().then(() => {
      this.pendingNotify = false;
      const snap = this.snapshot();
      for (const l of this.listeners) {
        try {
          l(snap);
        } catch (err) {
          // Listeners must not throw the store off-track; surface via console
          // and let Sentry pick it up via window.onerror.
          // eslint-disable-next-line no-console
          console.error('[OrderStore] listener threw', err);
        }
      }
    });
  }
}

/**
 * True when `a` carries a strictly later `updatedAt` than `b`. A copy without
 * a parseable `updatedAt` never wins, so a snapshot row is only ever rejected
 * in favour of state we can actually date.
 */
function isNewer(a: Order, b: Order): boolean {
  return updatedAtMs(a) > updatedAtMs(b);
}

function updatedAtMs(order: Order): number {
  if (!order.updatedAt) return -Infinity;
  const t = Date.parse(order.updatedAt);
  return Number.isFinite(t) ? t : -Infinity;
}
